/**
 * ConfirmDialog — the most common modal shape: a question with Cancel /
 * Confirm actions. Built on top of `Modal`.
 *
 * Use this for destructive confirmations (Let go, Delete, Archive) and any
 * yes/no decision that should block. For free-form modals, drop down to
 * `<Modal>` directly.
 *
 * Cancel is focused on open by default — the destructive action requires a
 * deliberate Tab. Set `initialFocus="confirm"` to flip that (e.g., a benign
 * Save prompt where confirming is the expected action).
 */

import { type ReactNode } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { t } from '@/lib/i18n';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Body content rendered between description and actions. */
  children?: ReactNode;
  /** Error from the last attempt — rendered as an Alert above the actions. */
  error?: string | null;
  /** Variant of the confirm button. Defaults to 'primary'. Use 'danger' for destructive ops. */
  confirmVariant?: 'primary' | 'danger';
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label while pending. Falls back to confirmLabel. */
  pendingLabel?: string;
  /** Disables both buttons while a request is in flight. */
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  error,
  confirmVariant = 'primary',
  cancelLabel,
  confirmLabel,
  pendingLabel,
  isPending = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPending) onCancel();
        onOpenChange(next);
      }}
      role="alertdialog"
      title={title}
      description={description}
      hideClose
      actions={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            {cancelLabel ?? t('Cancel')}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} isLoading={isPending}>
            {isPending
              ? (pendingLabel ?? confirmLabel ?? t('Confirm'))
              : (confirmLabel ?? t('Confirm'))}
          </Button>
        </>
      }
    >
      {children}
      {error ? <Alert variant="error">{error}</Alert> : null}
    </Modal>
  );
}
